import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-form';
import { validEmail } from '~/utils/forms';
import type { PhoneInputValue } from '~/admin/shared/forms/PhoneInput/PhoneInput';
import { validateStep } from '../helpers/validateFormFields';
import { getPlanSteps } from '../PlansStep';
import type { SelfSignUpForm } from '../types';
import { validPhoneNumber } from '../helpers/validatePhoneNumber';

const STORAGE_KEY = 'self_sign_up_form_data';
const EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

export type PersistedFormData = {
  values: Partial<SelfSignUpForm>;
  stepIndex: number;
  timestamp: number;
};

const isPhoneValid = async (phoneInput: PhoneInputValue | undefined): Promise<boolean> => {
  const { phone, inputValue, country } = phoneInput ?? {};

  if (typeof phone !== 'string' || typeof inputValue !== 'string' || country === undefined) {
    return false;
  }

  return !(await validPhoneNumber(phone, country.iso2));
};

export const sanitizePersistedValues = async (values: Partial<SelfSignUpForm>): Promise<Partial<SelfSignUpForm>> => {
  const { email, phone, ...rest } = values;

  return {
    ...rest,
    ...(email !== undefined && validEmail(email) === false && { email }),
    ...(phone !== undefined && (await isPhoneValid(phone)) && { phone }),
  };
};

export const getRecoveredStepIndex = (values: Partial<SelfSignUpForm>, savedStepIndex: number, productPlan?: string): number => {
  const stepsOrder = getPlanSteps(productPlan ?? 'starter').steps.map(s => s.name);
  let stepIndex = savedStepIndex;

  if (stepIndex > stepsOrder.length - 1) stepIndex = stepsOrder.length - 1;
  else if (stepIndex < 0) stepIndex = 0;

  for (let i = 0; i < stepIndex; i += 1) {
    const step = stepsOrder[i];
    if (!step || !(validateStep(step, values as SelfSignUpForm, productPlan))) {
      return i;
    }
  }

  return stepIndex;
};

export const getPersistedFormData = async (productPlan?: string): Promise<PersistedFormData | null> => {
  try {
    const item = localStorage.getItem(STORAGE_KEY);
    if (!item) return null;

    const data = JSON.parse(item) as unknown as PersistedFormData;

    if (Date.now() - data.timestamp > EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const sanitizedValues = await sanitizePersistedValues(data.values);
    return {
      ...data,
      stepIndex: getRecoveredStepIndex(sanitizedValues, data.stepIndex, productPlan),
      values: sanitizedValues,
    };
  } catch {
    return null;
  }
};

export const saveFormData = (values: Partial<SelfSignUpForm>, stepIndex: number): void => {
  try {
    if (typeof localStorage === 'undefined') return;

    const data: PersistedFormData = {
      stepIndex,
      timestamp: Date.now(),
      values,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore errors like "QuotaExceededError" or "SecurityError"
  }
};

export const clearPersistedFormData = (): void => {
  if (typeof localStorage === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore errors like "SecurityError"
  }
};

export const usePersistSelfSignUpForm = (stepIndex: number): void => {
  const isMountedRef = useRef<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const { values } = useFormContext<SelfSignUpForm>();

  const handleSave = useCallback(() => {
    if (typeof localStorage === 'undefined') return;

    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    saveFormData(values, stepIndex);
  }, [values, stepIndex]);

  useEffect(() => {
    timeoutRef.current = setTimeout(handleSave, 1000);

    return () => { clearTimeout(timeoutRef.current); };
  }, [values, stepIndex, handleSave]);
};
